/**
 * PortfolioAgent — Claude-style chat for the portfolio AI Asset Manager.
 *
 * Layout principles (mirrors claude.ai):
 *   • One scroll container — the message list scrolls; the page itself
 *     does not (no double scroll bars on either axis).
 *   • Assistant replies are FULL-WIDTH prose, no bubble or avatar block —
 *     reads like a document. A small subhead identifies "Lumeros".
 *   • User messages are right-aligned, a soft bubble.
 *   • Composer is a flat, refined multi-line textarea pinned to the
 *     bottom, with a contextual character/state line beneath. Borderless
 *     until focused.
 *   • Generous vertical rhythm (24–32px between turns); 16–17px text.
 *
 * Per-portfolio scope — URL is /portfolios/:portfolioId/agent. We POST
 * /api/portfolios/<id>/agent/ask with question + history each turn (sync).
 * History is Anthropic-shaped `{role, content}` for direct replay.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import KBPendingPanel from './KBPendingPanel';
import { useParams } from 'react-router-dom';
import {
  Alert, Avatar, Box, Chip, CircularProgress, IconButton, Stack, TextField,
  Tooltip, Typography,
  // Kept for the inline "Current" badge in the sidebar row; trim if a
  // future refactor drops the badge.
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import AgentChart from './AgentChart';

dayjs.extend(relativeTime);

// Sidebar geometry — pinned right column for chat history.
const SIDEBAR_WIDTH = 280;
const SIDEBAR_OPEN_KEY = 'veraxis-agent-history-open';
const readSidebarOpen = () => {
  try {
    const v = localStorage.getItem(SIDEBAR_OPEN_KEY);
    if (v == null) return true;          // default open on first visit
    return v === '1';
  } catch { return true; }
};
const writeSidebarOpen = (v) => {
  try { localStorage.setItem(SIDEBAR_OPEN_KEY, v ? '1' : '0'); } catch { /* noop */ }
};

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

// Conversation starters — surface as restrained chips on first load so the
// user has an on-ramp without feeling pushed.
const STARTERS = [
  'What\'s my revenue this month?',
  'Plot my revenue cliff over the next 5 years',
  'Which assets are underperforming?',
  'Show me my contracts and their strikes',
  'What obligations are due in the next 30 days?',
];

// Tool icons for the inline pills. Add entries as new tools land.
const TOOL_META = {
  list_portfolios:   { label: 'Listing portfolios',  emoji: '📁' },
  get_portfolio:     { label: 'Reading portfolio',   emoji: '🗂️' },
  get_overview:      { label: 'Pulling overview',    emoji: '📊' },
  get_revenue_cliff: { label: 'Projecting revenue',  emoji: '📈' },
  list_contracts:    { label: 'Loading contracts',   emoji: '📄' },
  get_calendar:      { label: 'Checking calendar',   emoji: '📅' },
};

// ReactMarkdown component overrides — `code` hijacks fenced ```chart blocks
// and routes to AgentChart; everything else falls through to the parent's
// CSS-styled defaults.
const MARKDOWN_COMPONENTS = {
  code({ inline, className, children }) {
    const lang = (className || '').match(/language-(\w+)/)?.[1];
    if (!inline && lang === 'chart') {
      return <AgentChart spec={String(children).trim()} />;
    }
    return <code className={className}>{children}</code>;
  },
};


// ── Local-history storage ────────────────────────────────────────────────────
//
// Sessions are persisted to localStorage per portfolio so a refresh restores
// the conversation and the user can switch back to an old chat. Server-side
// persistence comes next; this gives us the UX win immediately.
//
// Shape:
//   storageKey(portfolioId) → [
//     { id, title, messages, createdAt, updatedAt },
//     ...
//   ]   newest-updated first, capped at MAX_SESSIONS

const MAX_SESSIONS = 20;
const storageKey = (portfolioId) => `veraxis-agent-sessions-${portfolioId}`;

function loadSessions(portfolioId) {
  try {
    const raw = localStorage.getItem(storageKey(portfolioId));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveSessions(portfolioId, sessions) {
  try {
    localStorage.setItem(storageKey(portfolioId), JSON.stringify(sessions));
  } catch {
    /* localStorage full / unavailable — fail silently */
  }
}

function newSession() {
  return {
    id: `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title: null,                 // derived from first user msg
    messages: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function deriveTitle(messages) {
  // Use the first user message, trimmed to ~60 chars.
  const firstUser = (messages || []).find((m) => m.role === 'user');
  if (!firstUser) return null;
  const t = (firstUser.content || '').replace(/\s+/g, ' ').trim();
  return t.length > 60 ? t.slice(0, 60) + '…' : t;
}


export default function PortfolioAgent() {
  const { portfolioId } = useParams();

  // Sessions: full list (newest-updated first). `activeId` picks the one
  // currently rendered. Mutations go through `setSessions` so the
  // localStorage-sync effect picks them up.
  const [sessions, setSessions] = useState(() => {
    const stored = loadSessions(portfolioId);
    return stored.length ? stored : [newSession()];
  });
  const [activeId, setActiveId] = useState(() => sessions[0]?.id);

  const activeIndex   = sessions.findIndex((s) => s.id === activeId);
  const activeSession = sessions[activeIndex] || sessions[0];
  const messages      = activeSession?.messages || [];

  const [input, setInput]     = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError]     = useState(null);
  // Bumped after each successful agent turn so KBPendingPanel re-fetches
  // and surfaces any new entries the agent just proposed.
  const [kbRefreshKey, setKbRefreshKey] = useState(0);
  const scrollRef   = useRef(null);
  const composerRef = useRef(null);

  // Persist sessions any time they change. Skips when only the active
  // pointer changes (no state mutation on `sessions`).
  useEffect(() => {
    saveSessions(portfolioId, sessions);
  }, [portfolioId, sessions]);

  // Whenever the user navigates to a different portfolio, reload its
  // sessions from storage and pick the most-recently-updated one.
  useEffect(() => {
    const stored = loadSessions(portfolioId);
    const next = stored.length ? stored : [newSession()];
    setSessions(next);
    setActiveId(next[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  // ── Mutators ────────────────────────────────────────────────────────────
  const updateActive = useCallback((mut) => {
    setSessions((prev) => {
      const i = prev.findIndex((s) => s.id === activeId);
      if (i < 0) return prev;
      const updated = mut(prev[i]);
      // Re-sort by updatedAt desc so the active one floats to the top.
      const next = [
        { ...updated, updatedAt: Date.now() },
        ...prev.filter((_, idx) => idx !== i),
      ];
      return next.slice(0, MAX_SESSIONS);
    });
  }, [activeId]);

  const setMessages = useCallback((updater) => {
    updateActive((s) => {
      const nextMessages = typeof updater === 'function' ? updater(s.messages) : updater;
      return {
        ...s,
        messages: nextMessages,
        title: s.title || deriveTitle(nextMessages),
      };
    });
  }, [updateActive]);

  // Auto-scroll to bottom on new content. Skip if user scrolled up.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 240;
    if (nearBottom) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, sending]);

  // Auto-focus on mount + after each send so typing is uninterrupted.
  useEffect(() => {
    if (!sending) composerRef.current?.focus();
  }, [sending]);

  // Lock body scroll while the chat is mounted. The message column owns
  // scrolling; nothing else should bleed. AppLayout wraps us in a `main`
  // with py:2 padding + a Toolbar spacer, and our own height calc gets
  // those (so `main` fits viewport exactly) — but this is the belt-and-
  // braces guarantee that no peripheral content sneaks the page into
  // overflowing. Cleanup restores whatever the previous value was so
  // navigating elsewhere works normally.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Anthropic-shaped history for the backend (text-only roles).
  const wireHistory = useMemo(() => (
    messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }))
  ), [messages]);

  async function send(text) {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput('');
    setError(null);
    setSending(true);
    setMessages((prev) => [
      ...prev,
      { role: 'user',      content: q, at: Date.now() },
      { role: 'assistant', content: '', at: Date.now(), pending: true, tool_calls: [] },
    ]);

    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/agent/ask`,
        { question: q, history: wireHistory },
        { timeout: 120_000 },
      );
      const { answer, tool_calls, usage, stop_reason, rounds } = res.data;
      setMessages((prev) => {
        const next = prev.slice(0, -1);   // drop the pending bubble
        next.push({
          role:       'assistant',
          content:    answer || '(no answer)',
          at:         Date.now(),
          tool_calls: tool_calls || [],
          meta:       { usage, stop_reason, rounds },
        });
        return next;
      });
      // If the agent called kb_propose_entry this turn, the pending panel
      // needs to refetch. Cheaper than checking tool_calls — just bump
      // unconditionally; the panel returns null when count=0 anyway.
      setKbRefreshKey((k) => k + 1);
    } catch (e) {
      const detail =
        e.response?.data?.detail ||
        e.response?.data?.error  ||
        e.message ||
        'Something went wrong';
      setError(detail);
      setMessages((prev) => prev.filter((m) => !m.pending));
    } finally {
      setSending(false);
    }
  }

  function newChat() {
    // If the active session is already empty, just stay on it — no need
    // to pile up empties in history.
    if ((activeSession?.messages || []).length === 0) {
      setError(null);
      return;
    }
    setSessions((prev) => {
      const fresh = newSession();
      return [fresh, ...prev].slice(0, MAX_SESSIONS);
    });
    setActiveId((prev) => {
      // The new session is now at index 0 — set activeId to its id.
      // We use the functional setter trick because `sessions` will have
      // been replaced by the time React re-renders.
      return null;   // forces the effect below to pick the new active
    });
    setError(null);
  }
  // After newChat() clears activeId, this picks up the freshly-prepended
  // session so `activeIndex` resolves correctly. Runs once per session
  // list change while activeId is null.
  useEffect(() => {
    if (activeId == null && sessions[0]) setActiveId(sessions[0].id);
  }, [activeId, sessions]);

  function loadSession(id) {
    setError(null);
    setActiveId(id);
  }

  function deleteSession(id) {
    setSessions((prev) => {
      const next = prev.filter((s) => s.id !== id);
      // Always keep at least one (blank) session around so the UI has
      // something to render — saves a special "no active chat" state.
      return next.length ? next : [newSession()];
    });
    if (id === activeId) {
      setActiveId(null);   // effect picks the new first session
    }
  }

  const hasMessages = messages.length > 0;
  // History panel lists every session with at least one message,
  // INCLUDING the currently active one (highlighted). The active session
  // is shown so the user always knows which chat they're in — important
  // when the panel is the primary navigation between sessions.
  const historySessions = sessions.filter(
    (s) => (s.messages || []).length > 0
  );

  const [sidebarOpen, setSidebarOpen] = useState(readSidebarOpen);
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((v) => { writeSidebarOpen(!v); return !v; });
  }, []);

  return (
    // The component owns ALL vertical space below the AppBar. Layout is
    // [chat column | history sidebar]. Only the chat's message list and
    // the sidebar's list scroll — body stays locked (useEffect above).
    //
    // Height math: AppLayout's `main` has `py: 2` (32px top+bottom) plus
    // a `<Toolbar/>` spacer matching the AppBar (56px xs, 60px sm+).
    // Subtract both so main fits the viewport exactly and `body` never
    // grows its own scrollbar.
    <Box sx={{
      position: 'relative',
      height: { xs: 'calc(100vh - 88px)', sm: 'calc(100vh - 92px)' },
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      bgcolor: 'background.default',
      overflow: 'hidden',
    }}>
      {/* ─── Chat column (flex 1) ─────────────────────────────────────── */}
      <Box sx={{
        flex: 1, minWidth: 0,
        position: 'relative',
        display: 'flex', flexDirection: 'column',
        // When the sidebar is closed, leave a tiny "peek" affordance on
        // the right edge so the user can re-open it.
      }}>
      {/* ─── Scrollable message column ─────────────────────────────────── */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1, overflowY: 'auto', overflowX: 'hidden',
          // Bottom padding has to clear the composer's actual height
          // (the absolute-positioned wrapper is ~150–180px on a single-
          // line empty state plus the fade gradient + caption). Fixed
          // px so spacing doesn't drift with theme spacing changes.
          pb: '200px',
        }}
      >
        <Box sx={{
          maxWidth: 760, mx: 'auto',
          px: { xs: 2.5, sm: 4 },
          pt: { xs: 3, sm: 5 },
        }}>
          {hasMessages ? (
            <>
              <Header
                onNewChat={newChat}
                compact
                portfolioId={portfolioId}
                kbRefreshKey={kbRefreshKey}
              />
              <Stack spacing={4} sx={{ mt: 3 }}>
                {messages.map((m, i) => <Turn key={i} message={m} />)}
              </Stack>
            </>
          ) : (
            <>
              {/* Show pending-KB chip even on empty state so returning
                  users see what's awaiting review before they re-engage. */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <KBPendingPanel portfolioId={portfolioId} refreshKey={kbRefreshKey} />
              </Box>
              <EmptyHero onPick={(t) => send(t)} />
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            position: 'absolute', bottom: 96, left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 720, width: 'calc(100% - 32px)',
            zIndex: 2,
          }}
        >
          {error}
        </Alert>
      )}

      {/* ─── Composer (sticky, refined) ─────────────────────────────────── */}
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        // Soft fade so messages "dissolve" into the composer area.
        pointerEvents: 'none',
        background: (theme) =>
          `linear-gradient(180deg, transparent 0%, ${theme.palette.background.default} 40%)`,
        pt: 4, pb: 3,
      }}>
        <Box sx={{
          maxWidth: 760, mx: 'auto',
          px: { xs: 2.5, sm: 4 },
          pointerEvents: 'auto',
        }}>
          <Composer
            value={input}
            onChange={setInput}
            onSubmit={() => send()}
            sending={sending}
            inputRef={composerRef}
          />
          <Typography sx={{
            fontSize: '0.72rem', color: 'text.disabled', mt: 1,
            textAlign: 'center', letterSpacing: '0.01em',
          }}>
            Lumeros can make mistakes — verify numbers in the dashboards.
          </Typography>
        </Box>
      </Box>
      </Box>
      {/* ─── History sidebar (right) ──────────────────────────────────── */}
      <HistorySidebar
        open={sidebarOpen}
        onToggle={toggleSidebar}
        sessions={historySessions}
        activeId={activeId}
        onPick={loadSession}
        onDelete={deleteSession}
        onNewChat={newChat}
      />
    </Box>
  );
}


// ── Pieces ───────────────────────────────────────────────────────────────────

function Header({ onNewChat, compact, portfolioId, kbRefreshKey }) {
  // Past-chats navigation now lives in the persistent right sidebar
  // (HistorySidebar) — this header only carries the title + a new-chat
  // affordance + the KB pending pill for users who arrived via a
  // history link and want to start fresh without reaching for the
  // sidebar. The pill self-hides when there are no pending entries.
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Avatar
        variant="rounded"
        src="/Lumeros-favicon.png"
        alt="Lumeros"
        sx={{ width: 30, height: 30, bgcolor: 'transparent' }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: compact ? '0.92rem' : '1.05rem', fontWeight: 700, letterSpacing: '-0.005em' }}>
          AI Asset Manager
        </Typography>
      </Box>
      {portfolioId && (
        <KBPendingPanel portfolioId={portfolioId} refreshKey={kbRefreshKey} />
      )}
      {onNewChat && (
        <Tooltip title="New chat" arrow>
          <IconButton size="small" onClick={onNewChat}
                      sx={{ color: 'text.secondary' }}>
            <EditNoteIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}


// ── HistorySidebar ──────────────────────────────────────────────────────────
//
// Persistent right-side panel listing every chat for the current portfolio.
// Pattern follows Claude Desktop / ChatGPT: pinned column, scrollable list,
// active row highlighted, hover-revealed delete affordance, "New chat"
// button at the top. Collapse button slides the whole panel out so the
// chat can go full-width when you need focus.
//
// When collapsed: a thin vertical strip with a single ChevronLeft button
// pinned to the right edge so the user can re-open without leaving the
// page. The collapse state is persisted to localStorage (key
// SIDEBAR_OPEN_KEY) so the preference sticks across visits.
//
// Layout decision: the panel is in-flow (not absolute-positioned) so the
// chat column shrinks to make room. This keeps the composer + message
// centring honest (maxWidth + auto margin) and avoids overlap drama.

function HistorySidebar({ open, onToggle, sessions, activeId, onPick, onDelete, onNewChat }) {
  return (
    <Box sx={{
      flexShrink: 0,
      width: open ? SIDEBAR_WIDTH : 36,
      borderLeft: '1px solid',
      borderLeftColor: 'divider',
      bgcolor: 'background.paper',
      display: 'flex', flexDirection: 'column',
      transition: 'width 0.2s ease',
      overflow: 'hidden',
      // Distinct surface tone from the chat so the column reads as a
      // separate region even on themes with subtle dividers.
      boxShadow: open ? '-2px 0 8px rgba(0, 0, 0, 0.03)' : 'none',
    }}>
      {open ? (
        <>
          {/* Sidebar header — title + toggle + new-chat. */}
          <Stack direction="row" alignItems="center" sx={{
            px: 2, py: 1.5,
            borderBottom: '1px solid', borderBottomColor: 'divider',
          }}>
            <Typography sx={{
              flex: 1,
              fontSize: '0.78rem', fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>
              Chat history
            </Typography>
            <Tooltip title="New chat" arrow>
              <IconButton size="small" onClick={onNewChat} sx={{ color: 'text.secondary' }}>
                <EditNoteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Collapse" arrow>
              <IconButton size="small" onClick={onToggle} sx={{ color: 'text.secondary' }}>
                <ChevronRightIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Scrollable list. Inner padding-bottom matches the chat
              composer's clearance so a long list doesn't visually crash
              into the bottom edge. */}
          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
            {sessions.length === 0 ? (
              <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.82rem', color: 'text.disabled' }}>
                  No chats yet. Ask a question to start one.
                </Typography>
              </Box>
            ) : (
              sessions.map((s) => (
                <SidebarRow
                  key={s.id}
                  session={s}
                  isActive={s.id === activeId}
                  onPick={onPick}
                  onDelete={onDelete}
                />
              ))
            )}
          </Box>
        </>
      ) : (
        // Collapsed: just the expand chevron + a tiny "history" affordance
        // so the user knows where to click to bring the panel back.
        <Stack alignItems="center" sx={{ py: 1.5, gap: 0.5 }}>
          <Tooltip title="Show chat history" placement="left" arrow>
            <IconButton size="small" onClick={onToggle} sx={{ color: 'text.secondary' }}>
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Box>
  );
}


function SidebarRow({ session, isActive, onPick, onDelete }) {
  return (
    <Box
      onClick={() => { if (!isActive) onPick(session.id); }}
      sx={{
        position: 'relative',
        mx: 1, my: 0.25,
        px: 1.5, py: 1,
        borderRadius: 1.5,
        cursor: isActive ? 'default' : 'pointer',
        // Active row gets a teal-tint background + accent border so the
        // user always knows which chat they're in.
        bgcolor: isActive ? 'rgba(217, 138, 43, 0.10)' : 'transparent',
        border: '1px solid',
        borderColor: isActive ? 'rgba(217, 138, 43, 0.25)' : 'transparent',
        transition: 'background-color 0.12s ease, border-color 0.12s ease',
        '&:hover': {
          bgcolor: isActive ? 'rgba(217, 138, 43, 0.10)' : 'rgba(0, 0, 0, 0.025)',
          '& .row-delete': { opacity: 1 },
        },
      }}
    >
      <Box sx={{
        fontSize: '0.85rem', fontWeight: 500,
        color: 'text.primary', lineHeight: 1.35,
        // Two-line clamp so long first-messages don't blow up the row.
        display: '-webkit-box',
        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden', textOverflow: 'ellipsis',
        // Reserve room for the hover-delete button so it doesn't overlap
        // the title when revealed.
        pr: 3,
      }}>
        {session.title || '(empty chat)'}
      </Box>
      <Typography sx={{
        mt: 0.25,
        fontSize: '0.7rem', color: 'text.disabled',
      }}>
        {dayjs(session.updatedAt).fromNow()}
        {isActive && (
          <Box component="span" sx={{
            ml: 0.75, color: 'primary.main', fontWeight: 700,
            letterSpacing: '0.05em', textTransform: 'uppercase',
          }}>
            · current
          </Box>
        )}
      </Typography>

      {/* Hover-revealed delete affordance. Active session also gets it —
          you can clear the chat you're in (it'll be replaced with a fresh
          blank one). */}
      <IconButton
        className="row-delete"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(session.id);
        }}
        sx={{
          position: 'absolute', top: 6, right: 6,
          opacity: 0,
          transition: 'opacity 0.15s ease, color 0.15s ease',
          color: 'text.disabled',
          '&:hover': { color: 'error.main', bgcolor: 'transparent' },
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Box>
  );
}


function EmptyHero({ onPick }) {
  // The right-side history sidebar always shows past chats, so the
  // empty-hero no longer carries its own discovery affordance.
  return (
    <Box sx={{
      minHeight: 'calc(100vh - 220px)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      textAlign: 'center',
      position: 'relative',
    }}>

      <Avatar
        variant="rounded"
        src="/Lumeros-favicon.png"
        alt="Lumeros"
        sx={{
          width: 56, height: 56, mb: 3, bgcolor: 'transparent',
          boxShadow: '0 10px 30px rgba(217, 138, 43, 0.22)',
        }}
      />
      <Typography sx={{
        fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.015em',
        color: 'text.primary', mb: 1,
      }}>
        How can I help manage this portfolio?
      </Typography>
      <Typography sx={{
        fontSize: '0.95rem', color: 'text.secondary', maxWidth: 560, mb: 4,
        lineHeight: 1.6,
      }}>
        Ask anything an asset manager would — revenue, contracts, financials,
        capture price, obligations, asset performance. I pull the same live
        data the dashboards do, and source every number.
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center"
             sx={{ maxWidth: 640 }}>
        {STARTERS.map((s) => (
          <Chip
            key={s}
            label={s}
            onClick={() => onPick(s)}
            sx={{
              fontSize: '0.85rem', height: 34, px: 0.5,
              fontWeight: 500,
              border: '1px solid', borderColor: 'divider',
              bgcolor: 'background.paper',
              color: 'text.primary',
              transition: 'all 0.15s ease',
              '&:hover': {
                bgcolor: 'rgba(217, 138, 43, 0.06)',
                borderColor: 'primary.main',
                transform: 'translateY(-1px)',
              },
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}


function Turn({ message }) {
  const isUser = message.role === 'user';
  if (isUser) return <UserTurn message={message} />;
  return <AssistantTurn message={message} />;
}


function UserTurn({ message }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Box sx={{
        maxWidth: '85%',
        px: 2, py: 1.25,
        borderRadius: '18px',
        // Subtle purple tint to keep the eye flowing right-to-left without
        // shouting. Matches the user-avatar accent on hover states.
        bgcolor: 'rgba(233, 176, 101, 0.10)',
        color: 'text.primary',
        fontSize: '0.95rem', lineHeight: 1.55,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {message.content}
      </Box>
    </Box>
  );
}


function AssistantTurn({ message }) {
  return (
    <Box>
      {/* Speaker subhead — small, restrained. */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Box
          component="img"
          src="/Lumeros-favicon.png"
          alt="Lumeros"
          sx={{ width: 18, height: 18, borderRadius: '4px', display: 'block' }}
        />
        <Typography sx={{
          fontSize: '0.75rem', fontWeight: 600,
          color: 'text.secondary', letterSpacing: '0.02em',
        }}>
          Lumeros
        </Typography>
      </Stack>

      {/* Tool pills — inline, subtle. */}
      {message.tool_calls?.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ mb: 1.25, flexWrap: 'wrap', rowGap: 0.75 }}>
          {message.tool_calls.map((tc, i) => <ToolPill key={i} call={tc} />)}
        </Stack>
      )}

      {/* The reply itself — full-width prose, no bubble. */}
      {message.pending ? (
        <PendingBlock />
      ) : (
        <Box sx={MarkdownStyles}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
            {message.content || ''}
          </ReactMarkdown>
        </Box>
      )}

      {/* Tiny footer — only when reasoning was non-trivial. */}
      {!message.pending && message.meta?.rounds > 1 && (
        <Typography sx={{
          fontSize: '0.72rem', color: 'text.disabled', mt: 1.25,
          letterSpacing: '0.01em',
        }}>
          {message.meta.rounds} reasoning rounds
          {message.meta.usage?.input_tokens && (
            <> · {(message.meta.usage.input_tokens + (message.meta.usage.output_tokens || 0)).toLocaleString()} tokens</>
          )}
        </Typography>
      )}
    </Box>
  );
}


// Markdown styles applied to the assistant's reply container. Tuned for
// long-form reading: 1.65 line-height, generous between-element gap,
// well-styled code/tables. Same conventions as the rest of the app.
const MarkdownStyles = {
  fontSize: '0.95rem', lineHeight: 1.65, color: 'text.primary',
  wordBreak: 'break-word',
  '& > *:first-of-type': { mt: 0 },
  '& > *:last-of-type':  { mb: 0 },
  '& p':  { my: 1.25 },
  '& h1': { fontSize: '1.4rem',  fontWeight: 700, mt: 2.5, mb: 1, letterSpacing: '-0.01em' },
  '& h2': { fontSize: '1.2rem',  fontWeight: 700, mt: 2.5, mb: 1, letterSpacing: '-0.01em' },
  '& h3': { fontSize: '1.05rem', fontWeight: 700, mt: 2, mb: 0.75 },
  '& ul, & ol': { my: 1, pl: 3 },
  '& li': { mb: 0.5 },
  '& li > p': { my: 0.25 },
  '& blockquote': {
    borderLeft: '3px solid', borderLeftColor: 'primary.main',
    pl: 2, my: 1.5, color: 'text.secondary', fontStyle: 'italic',
  },
  '& code': {
    bgcolor: 'rgba(217, 138, 43, 0.08)',
    color: 'primary.main',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.85em',
    px: 0.6, py: 0.15, borderRadius: 0.6,
  },
  '& pre': {
    bgcolor: 'rgba(0, 0, 0, 0.30)',
    color: '#e7e7ea',
    p: 1.75, my: 1.5, borderRadius: 1.5,
    fontSize: '0.82rem', lineHeight: 1.55,
    overflowX: 'auto',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  '& pre code': {
    bgcolor: 'transparent', color: 'inherit',
    fontSize: 'inherit', p: 0,
  },
  '& table': {
    borderCollapse: 'collapse', my: 1.5, fontSize: '0.9rem',
    width: '100%',
  },
  '& th, & td': {
    border: '1px solid', borderColor: 'divider',
    px: 1.25, py: 0.75, textAlign: 'left',
  },
  '& th': { fontWeight: 700, bgcolor: 'rgba(217, 138, 43, 0.05)' },
  '& strong': { fontWeight: 700, color: 'text.primary' },
  '& em':     { fontStyle: 'italic' },
  '& a':      { color: 'primary.main', textDecoration: 'underline', textUnderlineOffset: 2 },
  '& hr':     { border: 'none', borderTop: '1px solid', borderTopColor: 'divider', my: 2 },
};


function PendingBlock() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
      <Box className="dots" sx={{
        display: 'flex', gap: 0.5, alignItems: 'center',
        '& span': {
          width: 6, height: 6, borderRadius: '50%',
          bgcolor: 'primary.main', opacity: 0.4,
          animation: 'agent-pulse 1.4s ease-in-out infinite',
        },
        '& span:nth-of-type(2)': { animationDelay: '0.2s' },
        '& span:nth-of-type(3)': { animationDelay: '0.4s' },
        '@keyframes agent-pulse': {
          '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
          '40%':           { opacity: 1,   transform: 'scale(1)'   },
        },
      }}>
        <span /><span /><span />
      </Box>
      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
        Thinking
      </Typography>
    </Box>
  );
}


function ToolPill({ call }) {
  const meta = TOOL_META[call.name] || { label: call.name, emoji: '🔧' };
  const failed = !!call.error;
  return (
    <Tooltip
      arrow
      enterDelay={400}
      title={
        <Box sx={{ p: 0.5, maxWidth: 360 }}>
          <Typography sx={{ fontSize: '0.7rem', fontFamily: 'ui-monospace, monospace', mb: 0.5 }}>
            {call.name}({JSON.stringify(call.input || {})})
          </Typography>
          {failed && (
            <Typography sx={{ fontSize: '0.7rem', color: '#fca5a5', mt: 0.5 }}>
              {call.error}
            </Typography>
          )}
          {!failed && call.duration_ms != null && (
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
              {call.duration_ms} ms · {(call.result_size || 0).toLocaleString()} B
            </Typography>
          )}
        </Box>
      }
    >
      <Chip
        size="small"
        label={
          <>
            <Box component="span" sx={{ mr: 0.5 }}>{meta.emoji}</Box>
            <Box component="span">{meta.label}</Box>
          </>
        }
        sx={{
          fontSize: '0.72rem', fontWeight: 500,
          height: 24,
          bgcolor: failed ? 'rgba(239, 68, 68, 0.10)' : 'rgba(217, 138, 43, 0.08)',
          color:   failed ? '#fca5a5' : 'primary.main',
          border: '1px solid',
          borderColor: failed ? 'rgba(239, 68, 68, 0.25)' : 'rgba(217, 138, 43, 0.20)',
          '& .MuiChip-label': { px: 0.75 },
          cursor: 'default',
        }}
      />
    </Tooltip>
  );
}


// ── Composer ─────────────────────────────────────────────────────────────────
// Modeled on Claude.ai — a flat, multi-line textarea that grows as the user
// types, with a circular send button anchored bottom-right. No childish
// rounded outline; subtle borders that brighten on focus.

function Composer({ value, onChange, onSubmit, sending, inputRef }) {
  return (
    <Box sx={{
      position: 'relative',
      // Outer surface: matte panel, soft elevation, restrained.
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: value || sending ? 'rgba(217, 138, 43, 0.40)' : 'divider',
      borderRadius: '20px',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
      '&:focus-within': {
        borderColor: 'primary.main',
        boxShadow: '0 4px 20px rgba(217, 138, 43, 0.18), 0 1px 2px rgba(0, 0, 0, 0.04)',
      },
    }}>
      <TextField
        inputRef={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
        }}
        placeholder="Ask anything about this portfolio…"
        fullWidth
        multiline
        minRows={1}
        maxRows={8}
        disabled={sending}
        variant="standard"
        InputProps={{
          disableUnderline: true,
          sx: {
            fontSize: '0.97rem',
            lineHeight: 1.55,
            px: 2.25, pt: 1.6, pb: 6,         // leave room for the send button
            color: 'text.primary',
            '&::placeholder': { color: 'text.disabled' },
          },
        }}
      />
      <Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
        <IconButton
          onClick={onSubmit}
          disabled={sending || !value.trim()}
          sx={{
            width: 34, height: 34,
            bgcolor: value.trim() && !sending ? 'primary.main' : 'rgba(0, 0, 0, 0.08)',
            color:   value.trim() && !sending ? '#F4F2EC' : 'text.disabled',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: value.trim() && !sending ? 'primary.dark' : 'rgba(0, 0, 0, 0.12)',
            },
            '&.Mui-disabled': {
              bgcolor: 'rgba(0, 0, 0, 0.08)',
              color: 'text.disabled',
            },
          }}
        >
          {sending
            ? <CircularProgress size={16} sx={{ color: 'primary.main' }} />
            : <ArrowUpwardIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>
    </Box>
  );
}
